import React, { ReactNode, useState } from "react";
import { useForm } from "@inertiajs/react";
import { new_session_path, registration_path } from "@/routes";
import { router } from "@inertiajs/react";
import { User } from "@/types/serializers";
import { Box, Flex, VStack, Input, Button, Text, HStack, Separator } from "@chakra-ui/react";
import DefaultLayout from "@/components/layouts/DefaultLayout";
import GoogleButton from "@/components/GoogleButton";
import signUpImage from "@/assets/sign-up-image.png";
import signUpImageAvif from "@/assets/sign-up-image.avif";
import { useAnalytics } from "@/hooks/useAnalytics";
import { SEOHead } from "@/components/SEOHead";

interface NewProps {
  user: User;
  errors?: Record<string, string[]>;
}

const New = ({ user, errors = {} }: NewProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const { data, setData, post, transform, processing } = useForm({
    first_name: user?.first_name ?? "",
    last_name: user?.last_name ?? "",
    email: user?.email ?? "",
    password: "",
    password_confirmation: "",
  });

  transform((data) => ({
    user: data,
  }));

  const TRACK = useAnalytics();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post(registration_path(), {
      onSuccess: () => {
        TRACK.USER_SIGNED_UP("email");
      },
    });
  };

  return (
    <>
      <SEOHead
        title="Sign Up - Muro"
        description="Create your Muro account and start reclaiming 5-15 hours of productive time weekly. Automated focus time blocking for deep work."
        canonical="/sign_up"
        noindex={true}
      />
      <Flex minH="100vh" direction={{ base: "column", md: "row" }}>
        <Flex
          flex={{ base: "1", md: "1" }}
          bg="white"
          alignItems="center"
          justifyContent="center"
          p={{ base: 6, md: 12 }}
        >
          <Box w="full" maxW="400px">
            <VStack gap={8} alignItems="flex-start" w="full">
              {/* Quote */}
              <Box w="full" pb={6} borderBottom="1px solid" borderColor="gray.200">
                <Text fontSize="sm" color="teal.600" fontStyle="italic" textAlign="center" lineHeight="1.6">
                  &ldquo;As you start to walk on the way, the way appears&rdquo;
                </Text>
                <Text fontSize="sm" color="teal.600" textAlign="center" mt={1}>
                  - Rumi
                </Text>
              </Box>

              {Object.keys(errors).length > 0 && (
                <Box bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="md" p={4} w="full">
                  <VStack as="ul" gap={1} alignItems="flex-start">
                    {Object.entries(errors).flatMap(([field, msgs]) =>
                      msgs.map((msg, idx) => (
                        <Text as="li" key={`${field}-${idx}`} fontSize="sm" color="red.600">
                          {msg}
                        </Text>
                      )),
                    )}
                  </VStack>
                </Box>
              )}

              <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                <VStack gap={4} w="full">
                  {/* Email field */}
                  <Box w="full">
                    <Text fontSize="sm" mb={2} color="gray.700">
                      Email
                    </Text>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder=""
                      value={data.email}
                      onChange={(e) => setData("email", e.target.value)}
                      bg="gray.100"
                      borderColor="gray.200"
                      _focus={{ borderColor: "teal.500", bg: "white" }}
                    />
                  </Box>

                  <Box w="full">
                    <Text fontSize="sm" mb={2} color="gray.700">
                      First Name
                    </Text>
                    <Input
                      id="first_name"
                      name="first_name"
                      type="text"
                      required
                      placeholder=""
                      value={data.first_name}
                      onChange={(e) => setData("first_name", e.target.value)}
                      bg="gray.100"
                      borderColor="gray.200"
                      _focus={{ borderColor: "teal.500", bg: "white" }}
                    />
                  </Box>

                  <Box w="full">
                    <Text fontSize="sm" mb={2} color="gray.700">
                      Last Name
                    </Text>
                    <Input
                      id="last_name"
                      name="last_name"
                      type="text"
                      required
                      placeholder=""
                      value={data.last_name}
                      onChange={(e) => setData("last_name", e.target.value)}
                      bg="gray.100"
                      borderColor="gray.200"
                      _focus={{ borderColor: "teal.500", bg: "white" }}
                    />
                  </Box>

                  <Box w="full">
                    <Text fontSize="sm" mb={2} color="gray.700">
                      Password
                    </Text>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      required
                      placeholder=""
                      value={data.password}
                      onChange={(e) => setData("password", e.target.value)}
                      bg="gray.100"
                      borderColor="gray.200"
                      _focus={{ borderColor: "teal.500", bg: "white" }}
                    />
                  </Box>

                  <Box w="full">
                    <Text fontSize="sm" mb={2} color="gray.700">
                      Confirm Password
                    </Text>
                    <Input
                      id="password_confirmation"
                      name="password_confirmation"
                      type="password"
                      required
                      placeholder=""
                      value={data.password_confirmation}
                      onChange={(e) => setData("password_confirmation", e.target.value)}
                      bg="gray.100"
                      borderColor="gray.200"
                      _focus={{ borderColor: "teal.500", bg: "white" }}
                    />
                  </Box>

                  <Button
                    type="submit"
                    disabled={processing}
                    w="full"
                    bg="teal.700"
                    color="white"
                    _hover={{ bg: "teal.800" }}
                    cursor="pointer"
                    mt={2}
                    size="lg"
                  >
                    {processing ? "Creating..." : "Signup"}
                  </Button>

                  <HStack w="full" my={2}>
                    <Separator flex="1" />
                    <Text fontSize="sm" color="gray.500" px={2}>
                      OR
                    </Text>
                    <Separator flex="1" />
                  </HStack>

                  <GoogleButton text="Sign up with Google" action="sign_up" />

                  <Box textAlign="center" w="full" mt={4}>
                    <Button
                      variant="plain"
                      type="button"
                      onClick={() => router.visit(new_session_path())}
                      color="gray.600"
                      _hover={{ color: "gray.800" }}
                      fontSize="sm"
                    >
                      Already have an account? Sign in
                    </Button>
                  </Box>
                </VStack>
              </form>
            </VStack>
          </Box>
        </Flex>

        <Box
          flex={{ base: "0", md: "1" }}
          position="relative"
          display={{ base: "none", md: "block" }}
          overflow="hidden"
          bg="teal.800"
        >
          <picture style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
            <source srcSet={signUpImageAvif} type="image/avif" />
            <img
              src={signUpImage}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                opacity: imageLoaded ? 1 : 0,
                transition: "opacity 700ms ease-in",
              }}
              fetchPriority="high"
              decoding="async"
              onLoad={() => setImageLoaded(true)}
            />
          </picture>
        </Box>
      </Flex>
    </>
  );
};

New.layout = (page: ReactNode) => <DefaultLayout>{page}</DefaultLayout>;

export default New;
